import { Inject, Injectable, InjectionToken } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { RetrieveResourceChildrenErrorCode, RetrieveResourceChildrenException } from '@protocols/use-cases';

import { AssignedResourcesEffects } from '../../../effects/assigned-resources.effects';
import { ResourceEffects } from '../../../effects/resource.effects';
import { AssignedResourcesState, ResourceContainer } from '../../../state/assigned-resources.state';
import { AssignedResourcesPageState } from '../../../state/assigned-resources-page.state';
import { ResourceData, ResourceState } from '../../../state/resource.state';
import { StagedResourcesState } from '../../../state/staged-resources.state';
import { ResourceListItem } from '../resources-list.component';

/**
 * The id of the container this manager instance edits. Provided per-manager (the
 * method type id for the root manager; a resource id for a nested one), so a
 * parent and a child manager — alive at the same time over one shared
 * `AssignedResourcesState` — each operate on their own node.
 */
export const RESOURCE_MANAGER_CONTAINER_ID = new InjectionToken<string>('RESOURCE_MANAGER_CONTAINER_ID');

/**
 * When provided, a NEW resource created from this manager pre-seeds and locks
 * the editor's type select to this value. The resource editor's session scope
 * provides `'response'` (its embedded mask manager only catalogs responses, so
 * creation is restricted to the same type); scopes that leave it unprovided
 * (e.g. the method-type editor) keep a free type select.
 */
export const RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK = new InjectionToken<string>(
  'RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK'
);

@Injectable()
export class ResourceManagerFacade {
  constructor(
    private readonly resourceEffects: ResourceEffects,
    private readonly resourceState: ResourceState,
    private readonly stagedState: StagedResourcesState,
    private readonly assignedState: AssignedResourcesState,
    private readonly assignedEffects: AssignedResourcesEffects,
    private readonly pageState: AssignedResourcesPageState,
    // Public: the component passes it to the editor dialog as the parent the
    // created draft is staged under.
    @Inject(RESOURCE_MANAGER_CONTAINER_ID) public readonly containerId: string
  ) {}

  // ============================================================================
  // Right list — assigned children of this container (the staged ground truth)
  // ============================================================================

  /** Page size for the assigned list, matching the catalog (left) list and the pipeline manager. */
  private static readonly ASSIGNED_PAGE_SIZE = 10;

  private readonly assignedChildren$ = this.assignedState.childrenOf(this.containerId);
  private readonly assignedIds$ = this.assignedChildren$.pipe(map((children) => new Set(children.map((c) => c.id))));

  /**
   * The full assigned set as list items. The right list shows only the current
   * page window of this (see {@link assignedResources$}); the left-list
   * `assigned` flag still derives from the full set via {@link assignedIds$}.
   */
  private readonly assignedResourcesAll$: Observable<ResourceListItem[]> = this.assignedChildren$.pipe(
    map((children) => children.map((c) => ({ id: c.id, name: c.kind === 'resource' ? c.name : '', assigned: true })))
  );

  public readonly assignedTotalCount$: Observable<number> = this.assignedResourcesAll$.pipe(
    map((items) => items.length)
  );

  /**
   * Clamp the requested page against the current total so an external shrink
   * (un-assigning items on a later page, a sibling/nested manager pruning the
   * tree) can never strand the list on an empty/out-of-range page. Everything
   * downstream reads this, not the raw stored index.
   */
  private readonly effectiveAssignedPageIndex$: Observable<number> = combineLatest([
    this.pageState.pageIndex$,
    this.assignedTotalCount$,
  ]).pipe(
    map(([index, total]) =>
      Math.min(index, Math.max(0, Math.ceil(total / ResourceManagerFacade.ASSIGNED_PAGE_SIZE) - 1))
    ),
    distinctUntilChanged()
  );

  /** The current page window of the assigned list (existing childrenOf order — no reorder). */
  public readonly assignedResources$: Observable<ResourceListItem[]> = combineLatest([
    this.assignedResourcesAll$,
    this.effectiveAssignedPageIndex$,
  ]).pipe(
    map(([all, index]) => {
      const start = index * ResourceManagerFacade.ASSIGNED_PAGE_SIZE;
      return all.slice(start, start + ResourceManagerFacade.ASSIGNED_PAGE_SIZE);
    })
  );

  public readonly assignedPageSize$: Observable<number> = of(ResourceManagerFacade.ASSIGNED_PAGE_SIZE);
  public readonly assignedPreviousTokens$: Observable<undefined[]> = this.effectiveAssignedPageIndex$.pipe(
    map((index) => new Array(index + 1).fill(undefined))
  );
  public readonly assignedNextToken$: Observable<undefined> = of(undefined);
  public readonly assignedCounting$: Observable<boolean> = of(false);

  /** Navigate the assigned list to a page (alias of the page store's updater; the facade holds no state). */
  public readonly goToAssignedPage = this.pageState.setPage;

  public readonly childrenLoading$ = this.assignedState.isLoadingChildrenOf(this.containerId);

  /**
   * Empty-state signal for the right list, mirroring the catalog store's
   * `noResources$` (empty AND not in-flight): true only once the children have
   * settled empty. The clamp guarantees a non-empty window whenever the total is
   * positive, so emptiness is determined off the total, not the current page.
   * While the lazy load is in flight this stays false, so the shared
   * `ts-resources-list` keeps its loading overlay instead of dropping straight to
   * the empty-state message.
   */
  public readonly noAssignedResources$: Observable<boolean> = combineLatest([
    this.assignedTotalCount$,
    this.childrenLoading$,
  ]).pipe(
    map(([total, loading]) => total === 0 && !loading),
    distinctUntilChanged()
  );

  /**
   * One event per failed lazy-load of a container's children, already mapped to
   * an i18n key (components only ever see localized keys) plus the failing
   * container's display name. The effect clears the state slot when an attempt
   * starts, so every failure — including a failed retry of the same container —
   * is a distinct emission: one snackbar per event.
   */
  public readonly childrenLoadFailed$: Observable<{ key: string; params: { name: string } }> =
    this.assignedState.childrenLoadError$.pipe(
      filter((failure): failure is { containerId: string; error: Error } => failure !== null),
      withLatestFrom(this.assignedState.containers$),
      map(([{ containerId, error }, containers]) => {
        const node = containers[containerId];
        // The node can be pruned (un-assigned) while its load is in flight
        const name = node && node.kind === 'resource' ? node.name : '';
        return { key: this.childrenLoadErrorToKey(error), params: { name } };
      })
    );

  /*
   * instanceof + code switch mirrors the editor facades' error mapping. The
   * single arm collapses onto the fallback today (one user-facing message);
   * the shape keeps future codes cheap.
   */
  private childrenLoadErrorToKey(error: Error): string {
    if (error instanceof RetrieveResourceChildrenException) {
      switch (error.code) {
        case RetrieveResourceChildrenErrorCode.REPOSITORY_ERROR:
          return 'resource_manager.errors.load_children_failed';
      }
    }
    return 'resource_manager.errors.load_children_failed';
  }

  // ============================================================================
  // Left list — global resource catalog, with the per-row `assigned` flag
  // derived from this container's staged children (workspace-members pattern)
  // ============================================================================

  public readonly availableResources$: Observable<ResourceListItem[]> = combineLatest([
    this.resourceState.resources$,
    this.assignedIds$,
  ]).pipe(
    map(([items, assignedIds]) => items.map((item: ResourceData) => ({ ...item, assigned: assignedIds.has(item.id) })))
  );

  public readonly searchTerm$ = this.resourceState.searchTerm$;
  public readonly totalCount$ = this.resourceState.totalCount$;
  public readonly pageSize$ = this.resourceState.pageSize$;
  public readonly nextResourceToken$ = this.resourceState.nextResourceToken$;
  public readonly previousResourceTokens$ = this.resourceState.previousResourceTokens$;
  public readonly noResources$ = this.resourceState.noResources$;
  public readonly counting$ = this.resourceState.counting$;
  public readonly loading$ = combineLatest([
    this.resourceState.loading$,
    this.resourceState.searching$,
    this.resourceState.counting$,
  ]).pipe(map(([loading, searching, counting]) => loading || searching || counting));

  // ============================================================================
  // Commands — effects defined on the per-manager ResourceState, plus passthrough
  // updater/effect aliases. (No public functions, no ComponentStore inheritance.)
  // ============================================================================

  /** Load this container's children (lazy, state-first) and the available catalog. */
  public readonly initialize = this.resourceState.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => {
        this.assignedEffects.ensureChildrenLoaded(this.containerId);
        this.resourceEffects.initializeResources();
      })
    )
  );

  /** Sets the catalog search term (alias of the list state's updater; triggers the reload). */
  public readonly search = this.resourceState.setSearchTerm;

  /**
   * Restricts the catalog to a single resource type. A fixed per-scope value set
   * once before {@link initialize} (no runtime toggle), so it does not trigger
   * its own reload — the initial load reads it from state. Alias of the list
   * state's updater.
   */
  public readonly setTypeFilter = this.resourceEffects.setTypeFilter;

  /** Loads the next/previous catalog page from the given cursor (alias of the list effect). */
  public readonly loadMore = this.resourceEffects.loadMoreResources;

  /** Toggle a left-list (catalog) row: assign it if not assigned, un-assign it if it is. */
  public readonly toggleAvailable = this.assignedState.effect((item$: Observable<ResourceListItem>) =>
    item$.pipe(
      tap((item: ResourceListItem) => {
        if (item.assigned) {
          this.assignedState.removeFrom({ parentId: this.containerId, childId: item.id });
        } else if (item.pending) {
          // A staged draft may have been pruned from the tree (un-assigned
          // everywhere); re-assigning it from the catalog must restore a draft node
          this.assignedState.stageDraft({ parentId: this.containerId, draft: { id: item.id, name: item.name } });
        } else {
          this.assignedState.assignTo({ parentId: this.containerId, child: { id: item.id, name: item.name } });
        }
      })
    )
  );

  /** Un-assign a right-list row. */
  public readonly unassign = this.assignedState.effect((item$: Observable<ResourceListItem>) =>
    item$.pipe(
      tap((item: ResourceListItem) => this.assignedState.removeFrom({ parentId: this.containerId, childId: item.id }))
    )
  );

  /**
   * Keeps the shared staging ledger in lockstep with the shared tree: `'save'`
   * nodes land in `pendingResources`, `'update'` nodes in `resourceOverrides`.
   *
   * ADDITIVE + NON-DESTRUCTIVE by design. Additive: a node leaving the tree
   * (un-assigned, pruned) is never removed from the ledger — the left list keeps
   * everything ever created, and useless entries are pruned by the persistence
   * responsible. Non-destructive: the tree only carries a node's display data
   * (name + nested count), so each sync passes ONLY those through
   * `stageFromTree`, which merges them onto any existing draft and leaves every
   * editor-owned attribute (`type`/`value`/`version`/`displayName`/...) intact.
   *
   * Hosted on the per-manager `resourceState` (not the shared ledger) so a closed
   * manager tears its subscription down instead of leaving a zombie watcher on
   * the shared `StagedResourcesState`.
   */
  private readonly syncStagedDrafts = this.resourceState.effect(() =>
    this.assignedState.containers$.pipe(
      tap((containers: Record<string, ResourceContainer>) => {
        for (const node of Object.values(containers)) {
          if (node.kind !== 'resource') continue;
          if (node.pendingPersistence === 'save') {
            this.stagedState.stageFromTree({
              id: node.id,
              name: node.name,
              nestedResourcesCount: node.childIds.length,
              mode: 'save',
            });
          } else if (node.pendingPersistence === 'update') {
            this.stagedState.stageFromTree({
              id: node.id,
              name: node.name,
              nestedResourcesCount: node.childIds.length,
              mode: 'update',
            });
          }
        }
      })
    )
  );
}
